import { useState } from "react";

interface Product {
  id: string;
  title: string;
  unitPrice: number;
  moqUnits: number;
  moqUSD: number;
  image: string;
  customizable: boolean;
}

interface Factory {
  name: string;
  flag: string;
  samplePrice: number;
  sampleLeadTime: string;
}

interface SampleOrderModalProps {
  factory: Factory;
  product: Product;
  onClose: () => void;
}

export default function SampleOrderModal({ factory, product, onClose }: SampleOrderModalProps) {
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", address: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const total = factory.samplePrice * qty;

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.country) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1200);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl w-full max-w-md p-10 text-center shadow-xl">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-500 text-3xl mx-auto mb-4">
            <i className="ri-flask-line" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900 mb-2">Sample Order Placed!</h2>
          <p className="text-gray-500 text-sm mb-2">
            <strong>{factory.name}</strong> will prepare your sample of <strong>{product.title}</strong>.
          </p>
          <p className="text-sm text-green-600 font-semibold mb-6">
            <i className="ri-time-line mr-1" />Expected delivery: {factory.sampleLeadTime}
          </p>
          <p className="text-xs text-gray-400 mb-6">A confirmation has been sent to {form.email}. The factory will contact you within 24 hours to confirm payment and shipping details.</p>
          <button onClick={onClose} className="px-8 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <i className="ri-flask-line text-green-500" /> Order Sample
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Product Preview */}
          <div className="flex gap-3 bg-green-50 border border-green-100 rounded-xl p-4">
            <img src={product.image} alt={product.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-gray-900">{product.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{factory.flag} {factory.name}</p>
              <p className="text-sm font-extrabold text-green-600 mt-1">${factory.samplePrice} per sample</p>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sample Quantity</label>
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <i className="ri-subtract-line" />
                </button>
                <span className="w-12 text-center text-sm font-bold">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <i className="ri-add-line" />
                </button>
              </div>
              <span className="text-sm text-gray-500">Total: <strong className="text-gray-900">${total.toFixed(2)}</strong></span>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Company</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Email *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Country *</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Your country" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Shipping Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full shipping address" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes to Factory (optional)</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} maxLength={500} placeholder="Specific requirements, color, size, branding notes..." className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400 resize-none" />
          </div>

          <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-sm text-green-800">
            <i className="ri-information-line mr-2" />
            <strong>Next step:</strong> After submitting, the factory will contact you within 24 hours to confirm payment (PayPal / Wire Transfer) and shipping details. Sample cost is deducted from your first bulk order.
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !form.name || !form.email || !form.country}
            className="w-full py-3.5 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {loading ? <span><i className="ri-loader-4-line animate-spin mr-2" />Submitting...</span> : <span><i className="ri-flask-line mr-2" />Place Sample Order — ${total.toFixed(2)}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
